import { dayjs } from '../../../core/dayjs/dayjs';

import { JOB_LOCATION_TYPES, JOB_LOCATION_TYPES_V3 } from '../../../global.constants';

// Requirement: Shared formatting utilities for dispatch operations report,
// copy-for-chat, and copy-for-spreadsheet functionality. These helpers ensure
// consistent formatting of job data (locations, times, inventory, crew summary)
// across all dispatch report outputs.

/**
 * Format arrival window from a start timestamp.
 * @param startTimestamp Unix timestamp in seconds
 * @param timezone IANA timezone string (e.g., "America/Vancouver")
 * @returns Formatted arrival window string (e.g., "8:00 AM - 9:00 AM")
 */
export function formatArrivalWindow(startTimestamp: number | null | undefined, timezone: string): string {
  if (!startTimestamp) return 'TBD';
  const start = dayjs.tz(Number(startTimestamp) * 1000, timezone);
  const end = start.add(1, 'hour');
  return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
}

/**
 * Format duration from start and end timestamps.
 * @param startTimestamp Unix timestamp in seconds
 * @param endTimestamp Unix timestamp in seconds
 * @param timezone IANA timezone string (e.g., "America/Vancouver")
 * @returns Formatted duration string (e.g., "2h 30m")
 */
export function formatDuration(
  startTimestamp: number | null | undefined,
  endTimestamp: number | null | undefined,
  timezone: string
): string {
  // Check for null/undefined explicitly (0 is a valid timestamp, though unlikely)
  if (startTimestamp == null || endTimestamp == null) return '0m';
  const start = dayjs.tz(Number(startTimestamp) * 1000, timezone);
  const end = dayjs.tz(Number(endTimestamp) * 1000, timezone);
  const diffMinutes = end.diff(start, 'minute');
  
  // Handle negative or zero durations (swapped timestamps or same time)
  if (diffMinutes <= 0) return '0m';
  
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * Format travel time from total minutes.
 * @param totalMinutes Total travel time in minutes
 * @returns Formatted travel time string (e.g., "1h 25m")
 */
export function formatTravelTime(totalMinutes: number): string {
  if (totalMinutes === 0) return '0 min';
  
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
}

/**
 * Get the display label for a location type.
 * @param type Location type key (e.g., 'start', 'end', 'office')
 * @returns Formatted label (e.g., 'Start', 'End', 'Office')
 */
export function getLocationLabel(type: string): string {
  const found = JOB_LOCATION_TYPES.find(l => l.key === type);
  const label = found ? found.label : type;
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : label;
}

/**
 * Parse crew summary from event's crewSummary field.
 * Handles various formats (JSON string, object with contents, Quill Delta format).
 * @param crewSummary The crewSummary value from the event
 * @returns Array of paragraph strings
 */
export function parseCrewSummary(crewSummary: any): string[] {
  if (!crewSummary) return [];

  // Handle cases where crewSummary may be a JSON string or an object
  let contents: any = null;

  // If crewSummary is a plain string, try to parse it
  if (typeof crewSummary === 'string') {
    try {
      const parsed = JSON.parse(crewSummary);
      contents = parsed.contents ?? parsed;
    } catch {
      // not JSON — attempt to treat as raw contents
      contents = crewSummary;
    }
  } else if (crewSummary.contents) {
    contents = crewSummary.contents;
  } else {
    contents = crewSummary;
  }

  // If contents is a string containing JSON, parse it
  let doc: any = null;
  if (typeof contents === 'string') {
    try {
      doc = JSON.parse(contents);
    } catch {
      doc = null;
    }
  } else {
    doc = contents;
  }

  // If doc has ops (Quill Delta format), extract insert values
  // Quill stores text in ops array, each op has an "insert" field
  // Newlines can be within a single insert or as separate ops
  if (doc && Array.isArray(doc.ops)) {
    // First, concatenate all insert values to get the full text
    const fullText = doc.ops
      .map((op: any) => (typeof op.insert === 'string' ? op.insert : ''))
      .join('');
    
    // Then split by newlines to get individual lines
    return fullText
      .split(/\r?\n/)
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);
  }

  // Fallback: if contents is plain text, split by newlines
  if (typeof contents === 'string') {
    return contents
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  return [];
}

/**
 * Parse inventory data from job fields or comments.
 * @param job The job object with fields and comments
 * @returns Inventory object with weight, volume, and rooms, or null
 */
export function parseInventory(job: any): { weight: number; volume: number; rooms: any[] } | null {
  if (!job) return null;

  // Inventory from field 'Inventory'
  const field = job.fields?.find((f: any) => f.name === 'jobs.inventory');
  if (field && field.values?.[0]?.value) {
    try {
      const data = JSON.parse(field.values[0].value);
      
      // Check for modern structure
      if (data.rooms && Array.isArray(data.rooms)) {
        return {
          weight: data.totalWeight || 0,
          volume: data.totalVolume || 0,
          rooms: data.rooms || []
        };
      }
      
      // Check for legacy/alternative structure
      if (Array.isArray(data)) {
        let totalWeight = 0;
        let totalVolume = 0;
        data.forEach((room: any) => {
          room.inventory?.forEach((item: any) => {
            totalWeight += (item.weight || 0) * (item.quantity || 1);
            totalVolume += (item.volume || 0) * (item.quantity || 1);
          });
        });
        
        return {
          weight: totalWeight,
          volume: totalVolume,
          rooms: data
        };
      }

      // Fallback for simple object
      return {
        weight: data.totalWeight || 0,
        volume: data.totalVolume || 0,
        rooms: data.rooms || []
      };
    } catch (e) {
      console.error('Failed to parse inventory from field', e);
    }
  }

  // Fallback: Inventory from comments (Yembo integration style)
  const generatedComment = job.comments?.comments?.find((c: any) => c.attributes?.includes('generated-inventory'));
  if (generatedComment && generatedComment.text) {
    try {
      const data = JSON.parse(generatedComment.text);
      return {
        weight: data.totalWeight || 0,
        volume: data.totalVolume || 0,
        rooms: data.rooms || []
      };
    } catch (e) {
      console.error('Failed to parse inventory from comment', e);
    }
  }
  
  return null;
}

/**
 * Get ordered and filtered locations for display (excludes dock).
 * @param eventLocations Locations from the event
 * @param jobLocations Locations from the job (fallback)
 * @returns Sorted array of locations
 */
export function getOrderedLocationsForDisplay(eventLocations: any[], jobLocations: any[]): any[] {
  let allLocations = eventLocations || [];

  // Fallback to job locations if available (ensures intermediate locations are included)
  if (jobLocations?.length) {
    allLocations = jobLocations.map((l: any) => ({
      ...l,
      type: l.locationType || l.type
    }));
  }

  if (!allLocations || allLocations.length === 0) return [];

  // Requirement: `#5762` - exclude storage (and internal dock staging) locations from dispatch crew-facing outputs.
  const filtered = allLocations.filter((l: any) => l.type !== 'dock' && l.type !== 'storage');

  // Sort by JOB_LOCATION_TYPES_V3 order
  const sorted = filtered.slice().sort((a: any, b: any) => {
    const indexOfA = JOB_LOCATION_TYPES_V3.indexOf(a.type);
    const indexOfB = JOB_LOCATION_TYPES_V3.indexOf(b.type);

    const sortValueOfA = indexOfA === -1 ? Number.MAX_SAFE_INTEGER : indexOfA;
    const sortValueOfB = indexOfB === -1 ? Number.MAX_SAFE_INTEGER : indexOfB;

    if (sortValueOfA !== sortValueOfB) {
      return sortValueOfA - sortValueOfB;
    }

    return allLocations.indexOf(a) - allLocations.indexOf(b);
  });

  return sorted;
}

/**
 * Get ordered location IDs for distance calculation (includes dock for return trip).
 * Order: dock → start → [intermediates] → end → dock
 * @param locations Job locations array
 * @returns Array of location IDs in order
 */
export function getOrderedLocationIdsForCalculation(locations: any[]): string[] {
  if (!locations || locations.length < 2) return [];

  // Find specific location types
  const dockLoc = locations.find(l => l.locationType === 'dock');
  const startLoc = locations.find(l => l.locationType === 'start');
  const endLoc = locations.find(l => l.locationType === 'end');
  
  // Requirement: `#5762` - exclude storage from intermediates so crews aren't routed through storage.
  // Get intermediate locations (not dock, start, end, or storage)
  const intermediates = locations.filter(l =>
    l.locationType && !['dock', 'start', 'end', 'storage'].includes(l.locationType)
  );
  
  // Sort intermediates by JOB_LOCATION_TYPES_V3 order
  const sortedIntermediates = intermediates.slice().sort((a, b) => {
    const indexOfA = JOB_LOCATION_TYPES_V3.indexOf(a.locationType!);
    const indexOfB = JOB_LOCATION_TYPES_V3.indexOf(b.locationType!);
    const sortValueOfA = indexOfA === -1 ? Number.MAX_SAFE_INTEGER : indexOfA;
    const sortValueOfB = indexOfB === -1 ? Number.MAX_SAFE_INTEGER : indexOfB;
    return sortValueOfA - sortValueOfB;
  });

  // Build ordered array: dock → start → intermediates → end → dock
  const orderedLocations: string[] = [];
  
  if (dockLoc?.location?.id) {
    orderedLocations.push(dockLoc.location.id);
  }
  if (startLoc?.location?.id) {
    orderedLocations.push(startLoc.location.id);
  }
  sortedIntermediates.forEach(loc => {
    if (loc.location?.id) {
      orderedLocations.push(loc.location.id);
    }
  });
  if (endLoc?.location?.id) {
    orderedLocations.push(endLoc.location.id);
  }
  // Add dock again for return trip
  if (dockLoc?.location?.id) {
    orderedLocations.push(dockLoc.location.id);
  }

  return orderedLocations;
}

/**
 * Convert distance from meters to display units.
 * @param meters Distance in meters
 * @param unit 'km' or 'mi'
 * @returns Distance in the specified unit
 */
export function convertMetersToDisplayUnit(meters: number, unit: 'km' | 'mi'): number {
  if (unit === 'km') {
    return meters / 1000;
  }
  return meters / 1609.344;
}

/**
 * Format distance with unit for display.
 * @param meters Distance in meters
 * @param unit 'km' or 'mi'
 * @param decimals Number of decimal places (default 2)
 * @returns Formatted string (e.g., "32.91 mi")
 */
export function formatDistance(meters: number, unit: 'km' | 'mi', decimals: number = 2): string {
  const distance = convertMetersToDisplayUnit(meters, unit);
  return `${distance.toFixed(decimals)} ${unit}`;
}

/**
 * Format inventory weight for display.
 * @param inventory Parsed inventory object
 * @returns Formatted weight string (e.g., "1500 lbs")
 */
export function formatInventoryWeight(inventory: { weight: number } | null): string {
  if (inventory) {
    return `${inventory.weight} lbs`;
  }
  return 'N/A';
}

/**
 * Format inventory volume for display.
 * @param inventory Parsed inventory object
 * @returns Formatted volume string (e.g., "500 ft³")
 */
export function formatInventoryVolume(inventory: { volume: number } | null): string {
  if (inventory) {
    return `${inventory.volume} ft³`;
  }
  return 'N/A';
}
